import { useState } from "react";
import { useLocation } from "wouter";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const loginMutation = trpc.adminAuth.login.useMutation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await loginMutation.mutateAsync({
        email,
        password,
      });

      // Store the admin JWT token locally for the admin session.
      if (result.adminToken) {
        localStorage.setItem("admin-jwt-token", result.adminToken);
        console.log("[AdminLogin] Admin JWT token stored in localStorage");
        // 触发自定义事件，让 useAuth 立即更新状态
        window.dispatchEvent(new Event("auth-token-updated"));
      }

      // Refresh the auth.me cache after a successful login.
      console.log("[AdminLogin] Login successful, refreshing auth cache...");
      await utils.auth.me.invalidate();
      
      // Allow the auth state to settle before navigating.
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Redirect to the product inventory management page.
      console.log("[AdminLogin] Navigating to admin inventory page...");
      navigate("/admin/inventory", { replace: true });
    } catch (err: any) {
      console.error("[AdminLogin] Login error:", err);
      const errorMessage = err?.data?.message || err?.message || "Login failed";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
          <p className="text-gray-600">Administrator access</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading || !email || !password}
            className="w-full"
          >
            {isLoading ? "Logging in..." : "Login"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/", { replace: true })}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Back to Home
          </Button>
        </div>
      </Card>
    </div>
  );
}
